/**
 * Pre-Performance Ritual Component
 * A 90-120 second mental preparation sequence before Performance Mode sessions.
 * 
 * Audio-driven phase progression with fallback timers.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  Wind, 
  Sparkles, 
  Eye, 
  Target, 
  Zap,
  SkipForward,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Loader2
} from "lucide-react";
import AudioWaveform from "../AudioWaveform";
import { voiceVolume, getStoredAudioMode } from "../../utils/modeAudio";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase configuration
const PHASE_CONFIG = {
  reset: { icon: Wind, color: "from-blue-500 to-cyan-500" },
  identity: { icon: Sparkles, color: "from-orange-500 to-amber-500" },
  visualization: { icon: Eye, color: "from-purple-500 to-violet-500" },
  anchor: { icon: Target, color: "from-emerald-500 to-teal-500" },
  activation: { icon: Zap, color: "from-red-500 to-orange-500" }
};

const PrePerformanceRitual = ({
  sport,
  ritual,
  onComplete,
  onSkip,
  authToken
}) => {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Preparing your ritual...");
  const [isReady, setIsReady] = useState(false); // Audio loaded, waiting for user to start
  const [hasStarted, setHasStarted] = useState(false); // User clicked start
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  
  // Refs
  const audioDataRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const timerRef = useRef(null);
  const lineTimerRef = useRef(null);
  const phaseTimerRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Derived state
  const phases = ritual?.phases || [];
  const currentPhase = phases[currentPhaseIndex];
  const totalDuration = ritual?.total_duration || 105;
  const progress = Math.min((elapsedTime / totalDuration) * 100, 100);
  const currentLines = currentPhase?.script || [];
  const currentLine = currentLines[currentLineIndex] || "";
  
  // Get auth token
  const getToken = useCallback(() => {
    return authToken || localStorage.getItem("token");
  }, [authToken]);

  // Cleanup
  const cleanup = useCallback(() => {
    mountedRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    if (lineTimerRef.current) {
      clearInterval(lineTimerRef.current);
      clearTimeout(lineTimerRef.current);
    }
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.src = "";
      voiceAudioRef.current = null;
    }
  }, []);

  // Load audio on mount
  useEffect(() => {
    mountedRef.current = true;
    loadRitualAudio();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport]);

  const loadRitualAudio = async () => {
    console.log("[Ritual] Loading audio for sport:", sport);
    
    try {
      setIsLoading(true);
      setLoadingMessage("Preparing your ritual...");
      
      // Fetch ritual audio data (includes pre-recorded URLs)
      const response = await axios.post(
        `${API_URL}/api/private-suite/ritual/${sport}/generate-audio?force=true`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (!mountedRef.current) return;
      
      const audio = response.data.audio;
      audioDataRef.current = audio;
      
      // Check for pre-recorded audio (Michael's voice)
      if (audio?.prerecorded?.sport_affirmations) {
        console.log("[Ritual] Using Michael's pre-recorded sport affirmations");
        audioDataRef.current.usePrerecorded = true;
        audioDataRef.current.prerecordedUrl = audio.prerecorded.sport_affirmations;
      }
      
      console.log("[Ritual] Audio loaded:", {
        phases: audio?.phases?.length,
        hasAudio: audio?.phases?.map(p => !!p.audio_url),
        hasPrerecorded: !!audio?.prerecorded?.sport_affirmations
      });
      
      setLoadingMessage("Audio ready. Starting ritual...");
      
      // Show start button instead of auto-starting
      setTimeout(() => {
        if (!mountedRef.current) return;
        setIsLoading(false);
        setIsReady(true);
      }, 500);
      
    } catch (err) {
      console.error("[Ritual] Error loading audio:", err);
      if (!mountedRef.current) return;
      setIsLoading(false);
      setIsReady(true);
      setAudioError(true);
    }
  };

  // User clicks start - this satisfies browser autoplay policy
  const handleStart = () => {
    setHasStarted(true);
    setIsReady(false);
    startRitual();
  };

  const startRitual = () => {
    console.log("[Ritual] Starting ritual playback");
    
    // Elapsed time counter
    timerRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    // Start first phase
    runPhase(0);
  };

  const runPhase = (phaseIdx) => {
    if (!mountedRef.current) return;
    
    if (phaseIdx >= phases.length) {
      console.log("[Ritual] All phases complete");
      cleanup();
      onComplete();
      return;
    }
    
    const phase = phases[phaseIdx];
    const phaseAudio = audioDataRef.current?.phases?.[phaseIdx];
    
    console.log(`[Ritual] === Phase ${phaseIdx}: ${phase.name} ===`);
    console.log(`[Ritual] Has audio: ${!!phaseAudio?.audio_url}, Has prerecorded: ${!!phaseAudio?.use_prerecorded}`);
    
    setCurrentPhaseIndex(phaseIdx);
    setCurrentLineIndex(0);
    
    // For IDENTITY phase with pre-recorded affirmations, play them in sequence
    if (phase.id === "identity" && phaseAudio?.use_prerecorded && phaseAudio?.prerecorded_affirmations?.length > 0) {
      console.log(`[Ritual] IDENTITY phase - Using ${phaseAudio.prerecorded_affirmations.length} pre-recorded affirmations`);
      playPrerecordedAffirmations(phaseAudio.prerecorded_affirmations, phase, () => {
        console.log(`[Ritual] Phase ${phaseIdx} (prerecorded) complete`);
        runPhase(phaseIdx + 1);
      });
      return;
    }
    
    // Try to play audio - text advancement is started AFTER we know the audio duration
    if (phaseAudio?.audio_url) {
      playPhaseAudio(phaseAudio, phase, () => {
        // Audio finished - advance to next phase
        console.log(`[Ritual] Phase ${phaseIdx} audio complete`);
        runPhase(phaseIdx + 1);
      });
    } else {
      // No audio - use timer based on phase duration and start line advancement
      console.log(`[Ritual] No audio, using ${phase.duration}s timer`);
      runLineAdvancement(phase, phase.duration * 1000);
      phaseTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        runPhase(phaseIdx + 1);
      }, phase.duration * 1000);
    }
  };
  
  // Play pre-recorded affirmations in sequence
  const playPrerecordedAffirmations = (affirmations, phase, onComplete) => {
    let currentIndex = 0;
    
    const playNext = () => {
      if (!mountedRef.current) return;
      if (currentIndex >= affirmations.length) {
        onComplete();
        return;
      }
      
      const aff = affirmations[currentIndex];
      const audioUrl = `${API_URL}${aff.audio_url}`;
      
      console.log(`[Ritual] Playing affirmation ${currentIndex + 1}/${affirmations.length}: "${aff.text?.substring(0, 30)}..."`);
      setCurrentLineIndex(currentIndex);
      
      // Stop any existing audio
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
      }
      
      const audio = new Audio(audioUrl);
      voiceAudioRef.current = audio;
      audio.volume = voiceVolume(getStoredAudioMode());
      
      audio.onended = () => {
        currentIndex++;
        // Brief pause between affirmations
        setTimeout(playNext, 800);
      };
      
      audio.onerror = () => {
        console.error(`[Ritual] Error playing affirmation audio: ${audioUrl}`);
        currentIndex++;
        setTimeout(playNext, 500);
      };
      
      audio.play().catch(err => {
        console.error(`[Ritual] Autoplay blocked:`, err);
        currentIndex++;
        setTimeout(playNext, 500);
      });
    };
    
    playNext();
  };

  const runLineAdvancement = (phase, durationMs, audioElement = null) => {
    if (!phase?.script?.length) return;
    
    // Clear any existing timer
    if (lineTimerRef.current) clearInterval(lineTimerRef.current);
    
    const lineCount = phase.script.length;
    const lineInterval = durationMs / lineCount;
    
    console.log(`[Ritual] Line advancement: ${lineCount} lines over ${durationMs}ms (${lineInterval}ms each)`);
    
    // If we have an audio element, sync lines to audio playback time
    if (audioElement) {
      let lastLineIndex = 0;
      lineTimerRef.current = setInterval(() => {
        if (!mountedRef.current) {
          clearInterval(lineTimerRef.current);
          return;
        }
        
        // Sync to audio currentTime
        const audioTime = audioElement.currentTime * 1000; // Convert to ms
        const expectedLine = Math.floor(audioTime / lineInterval);
        
        if (expectedLine !== lastLineIndex && expectedLine < lineCount) {
          lastLineIndex = expectedLine;
          setCurrentLineIndex(expectedLine);
        }
      }, 50); // Check frequently for tight sync
    } else {
      // Fallback: timer-based advancement
      let currentLine = 0;
      
      const advanceLine = () => {
        if (!mountedRef.current) return;
        currentLine++;
        if (currentLine < lineCount) {
          setCurrentLineIndex(currentLine);
          lineTimerRef.current = setTimeout(advanceLine, lineInterval);
        }
      };
      
      // First advance after one interval
      lineTimerRef.current = setTimeout(advanceLine, lineInterval);
    }
  };

  const playPhaseAudio = (phaseAudio, phase, onPhaseComplete) => {
    // Stop any existing audio
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.src = "";
    }
    
    const audio = new Audio();
    voiceAudioRef.current = audio;
    
    let audioCompleted = false;
    let fallbackTimer = null;
    
    // Handle both base64 (ElevenLabs) and URL paths (pre-recorded files)
    let audioSrc = phaseAudio.audio_url;
    
    // If it's a URL path (starts with /), prepend the API URL
    if (audioSrc && audioSrc.startsWith('/api/')) {
      audioSrc = `${API_URL}${audioSrc}`;
      console.log(`[Ritual] Playing pre-recorded audio for ${phase.name}:`, audioSrc);
    } else {
      console.log(`[Ritual] Playing generated audio for ${phase.name}`);
    }
    
    // Called when audio is loaded - we can get the actual duration
    audio.onloadedmetadata = () => {
      if (!mountedRef.current) return;
      
      const actualDuration = audio.duration * 1000; // Convert to ms
      console.log(`[Ritual] Audio loaded: ${phase.name}, duration: ${audio.duration.toFixed(1)}s`);
      
      // Now start line advancement based on ACTUAL audio duration, synced to audio playback
      runLineAdvancement(phase, actualDuration, audio);
      
      // Set up fallback timer with actual duration + buffer
      fallbackTimer = setTimeout(() => {
        if (!audioCompleted && mountedRef.current) {
          console.log(`[Ritual] Fallback timer triggered for ${phase.name}`);
          audioCompleted = true;
          setAudioPlaying(false);
          onPhaseComplete();
        }
      }, actualDuration + 5000);
    };
    
    audio.onplay = () => {
      console.log(`[Ritual] Audio playing: ${phase.name}`);
      setAudioPlaying(true);
    };
    
    audio.onended = () => {
      if (audioCompleted) return;
      audioCompleted = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      
      console.log(`[Ritual] Audio ended: ${phase.name}`);
      setAudioPlaying(false);
      
      // Check for user affirmation (anchor phase)
      if (phaseAudio.user_affirmation_audio) {
        console.log("[Ritual] Playing user affirmation");
        playUserAffirmation(phaseAudio.user_affirmation_audio, onPhaseComplete);
      } else {
        onPhaseComplete();
      }
    };
    
    audio.onerror = (e) => {
      if (audioCompleted) return;
      audioCompleted = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      
      console.error(`[Ritual] Audio error for ${phase.name}:`, e);
      setAudioPlaying(false);
      
      // Start line advancement with fallback duration
      runLineAdvancement(phase, phase.duration * 1000);
      
      // Continue after the phase duration
      setTimeout(onPhaseComplete, phase.duration * 1000);
    };
    
    audio.volume = voiceVolume(getStoredAudioMode());
    audio.src = audioSrc;
    
    audio.play()
      .then(() => {
        console.log(`[Ritual] Audio play() success: ${phase.name}`);
      })
      .catch(err => {
        if (audioCompleted) return;
        audioCompleted = true;
        if (fallbackTimer) clearTimeout(fallbackTimer);
        
        console.error(`[Ritual] Audio play() failed for ${phase.name}:`, err.message);
        setAudioPlaying(false);
        setAudioError(true);
        
        // Browser blocked autoplay - use phase duration
        console.log(`[Ritual] Using ${phase.duration}s fallback`);
        runLineAdvancement(phase, phase.duration * 1000);
        setTimeout(onPhaseComplete, phase.duration * 1000);
      });
  };

  const playUserAffirmation = (audioUrl, onComplete) => {
    const audio = new Audio();
    audio.volume = voiceVolume(getStoredAudioMode());
    audio.src = audioUrl;
    
    audio.onended = onComplete;
    audio.onerror = () => {
      console.log("[Ritual] User affirmation error");
      onComplete();
    };
    
    audio.play().catch(() => {
      console.log("[Ritual] User affirmation play failed");
      setTimeout(onComplete, 2000);
    });
  };

  const togglePause = () => {
    if (isPaused) {
      if (voiceAudioRef.current) voiceAudioRef.current.play();
      timerRef.current = setInterval(() => {
        if (!mountedRef.current) return;
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (voiceAudioRef.current) voiceAudioRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
    }
    setIsPaused(!isPaused);
  };

  const handleSkip = () => {
    cleanup();
    onSkip();
  };

  // UI helpers
  const phaseConfig = currentPhase ? PHASE_CONFIG[currentPhase.id] || PHASE_CONFIG.reset : PHASE_CONFIG.reset;
  const PhaseIcon = phaseConfig.icon;
  const phaseColor = phaseConfig.color;

  // Loading state
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f1a] flex flex-col items-center justify-center"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="mb-6"
        >
          <Loader2 size={48} className="text-orange-500" />
        </motion.div>
        <p className="text-white/70 text-center px-8">{loadingMessage}</p>
        
        <button
          onClick={handleSkip}
          className="mt-8 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 transition-all flex items-center gap-2"
          data-testid="ritual-loading-skip-btn"
        >
          <SkipForward size={18} />
          <span>Skip to Session</span>
        </button>
      </motion.div>
    );
  }

  // Ready state - waiting for user to tap Start (required for browser audio policy)
  if (isReady && !hasStarted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f1a] flex flex-col items-center justify-center px-8"
      >
        {/* Animated icon */}
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="relative mb-8"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500/30 to-red-500/30 flex items-center justify-center">
            <Zap size={40} className="text-orange-400" />
          </div>
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 w-24 h-24 rounded-full bg-orange-500/20 blur-xl -z-10"
          />
        </motion.div>

        <h2 className="text-2xl text-white font-light mb-3">Ready to Prime</h2>
        <p className="text-white/50 text-center mb-8 max-w-sm">
          Your personalized {sport} ritual is ready. Tap below to begin your mental preparation.
        </p>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={handleStart}
          className="px-10 py-4 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all flex items-center gap-3"
          data-testid="ritual-start-btn"
        >
          <Play size={20} className="ml-0.5" />
          <span>Begin Ritual</span>
        </motion.button>

        <button
          onClick={handleSkip}
          className="mt-6 text-white/40 hover:text-white/60 text-sm transition-colors"
          data-testid="ritual-skip-ready-btn"
        >
          Skip to Session
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f1a] flex flex-col"
    >
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <button
          onClick={handleSkip}
          className="text-white/60 hover:text-white flex items-center gap-2"
          data-testid="ritual-skip-btn"
        >
          <ArrowLeft size={20} />
          <span className="text-sm">Skip</span>
        </button>
        
        <div className="flex items-center gap-3">
          {audioPlaying ? (
            <div className="flex items-center gap-2">
              <AudioWaveform isPlaying={true} barCount={5} color="#4ade80" size="sm" />
              <Volume2 size={16} className="text-green-400" />
            </div>
          ) : audioError ? (
            <VolumeX size={16} className="text-red-400/50" />
          ) : (
            <VolumeX size={16} className="text-white/30" />
          )}
          <span className="text-white/50 text-sm font-mono">
            {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
          </span>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="px-6 mb-6">
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className={`h-full bg-gradient-to-r ${phaseColor}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        {/* Phase indicators */}
        <div className="flex justify-between mt-3">
          {phases.map((phase, idx) => {
            const config = PHASE_CONFIG[phase.id] || PHASE_CONFIG.reset;
            const Icon = config.icon;
            const isActive = idx === currentPhaseIndex;
            const isComplete = idx < currentPhaseIndex;
            
            return (
              <div
                key={phase.id}
                className={`flex flex-col items-center transition-opacity duration-300 ${
                  isActive ? "opacity-100" : isComplete ? "opacity-60" : "opacity-30"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isActive 
                    ? `bg-gradient-to-br ${config.color} shadow-lg` 
                    : isComplete 
                      ? "bg-white/20" 
                      : "bg-white/5"
                }`}>
                  <Icon size={14} className="text-white" />
                </div>
                <span className="text-[10px] text-white/50 mt-1 hidden sm:block">
                  {phase.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        {/* Phase icon with glow */}
        <motion.div
          key={currentPhaseIndex}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="relative mb-8"
        >
          {/* Glow effect */}
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className={`absolute inset-0 w-28 h-28 -m-2 rounded-full bg-gradient-to-br ${phaseColor} blur-xl`}
          />
          
          {/* Icon */}
          <div className={`relative w-24 h-24 rounded-full bg-gradient-to-br ${phaseColor} flex items-center justify-center shadow-2xl`}>
            <PhaseIcon size={36} className="text-white" />
          </div>
        </motion.div>
        
        {/* Phase name */}
        <motion.h2
          key={`name-${currentPhaseIndex}`}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-2xl text-white mb-3 font-light"
        >
          {currentPhase?.name}
        </motion.h2>
        
        {/* Voice indicator */}
        {currentPhase && (
          <div className={`px-3 py-1 rounded-full text-xs mb-6 ${
            currentPhase.voice === "user" 
              ? "bg-orange-500/20 text-orange-300 border border-orange-500/30" 
              : "bg-white/10 text-white/60 border border-white/10"
          }`}>
            {currentPhase.voice === "user" ? "Your Voice" : "Michael"}
          </div>
        )}
        
        {/* Current line */}
        <AnimatePresence mode="wait">
          <motion.p
            key={`${currentPhaseIndex}-${currentLineIndex}`}
            initial={{ y: 15, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -15, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="text-white/85 text-lg text-center max-w-md leading-relaxed font-light"
          >
            {currentLine}
          </motion.p>
        </AnimatePresence>
        
        {/* User affirmation for anchor phase */}
        {currentPhase?.id === "anchor" && currentPhase.user_affirmation && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20"
          >
            <p className="text-orange-300/90 text-sm text-center italic">
              "{currentPhase.user_affirmation}"
            </p>
          </motion.div>
        )}
      </div>
      
      {/* Controls */}
      <div className="p-6 flex justify-center gap-4">
        <button
          onClick={togglePause}
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          data-testid="ritual-pause-btn"
        >
          {isPaused ? (
            <Play size={20} className="text-white ml-0.5" />
          ) : (
            <Pause size={20} className="text-white" />
          )}
        </button>
        
        <button
          onClick={handleSkip}
          className="px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2 text-sm"
          data-testid="ritual-skip-to-session-btn"
        >
          <SkipForward size={16} />
          <span>Skip to Session</span>
        </button>
      </div>
    </motion.div>
  );
};

export default PrePerformanceRitual;
